import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import React from 'react';

import { cn } from './cn';

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-w-sm rounded-lg border border-border bg-surface px-3 py-2 text-sm text-gray-700 shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        className
      )}
      {...props}
    >
      {children}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
  className?: string;
  /** Class for the trigger wrapper span — e.g. `min-w-0 max-w-full` so children can truncate. */
  triggerClassName?: string;
  /** Allow interactive content inside the tooltip (prevents auto-close on hover) */
  interactive?: boolean;
}

export const Tooltip = ({
  content,
  children,
  side = 'top',
  delayDuration = 200,
  className,
  triggerClassName,
  interactive = false,
}: TooltipProps) => {
  const [open, setOpen] = React.useState(false);
  const dismissTimeout = React.useRef<ReturnType<typeof setTimeout>>();

  const scheduleClose = React.useCallback(() => {
    dismissTimeout.current = setTimeout(() => setOpen(false), 500);
  }, []);

  const cancelClose = React.useCallback(() => {
    if (dismissTimeout.current) {
      clearTimeout(dismissTimeout.current);
      dismissTimeout.current = undefined;
    }
  }, []);

  React.useEffect(() => {
    return () => cancelClose();
  }, [cancelClose]);

  if (interactive) {
    return (
      <TooltipProvider>
        <TooltipRoot open={open} onOpenChange={setOpen} delayDuration={delayDuration}>
          <TooltipTrigger asChild>
            <span
              className={cn('inline-flex', triggerClassName)}
              onMouseEnter={() => {
                cancelClose();
                setOpen(true);
              }}
              onMouseLeave={scheduleClose}
            >
              {children}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side={side}
            className={className}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onPointerDownOutside={() => {
              cancelClose();
              setOpen(false);
            }}
          >
            {content}
          </TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <TooltipRoot delayDuration={delayDuration}>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex', triggerClassName)}>{children}</span>
        </TooltipTrigger>
        <TooltipContent side={side} className={className}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};

export { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger };
